import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Shield,
  ShieldAlert,
  AlertTriangle,
  AlertCircle,
  Target,
  TrendingUp,
  MessageSquare,
  Calendar,
  Phone,
  Video,
  Mail,
  MapPin,
  Send,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronRight,
  ExternalLink,
  Lightbulb,
  FileText,
  Users,
  Building,
  Star,
  Zap,
  HeadphonesIcon,
  ArrowRight,
  Activity,
  BarChart3,
  PieChart,
  Bell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VirtualCISO = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Message form state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageForm, setMessageForm] = useState({
    subject: '',
    message: '',
    priority: 'normal',
    category: 'general'
  });
  
  // Consultation form state
  const [showConsultModal, setShowConsultModal] = useState(false);
  const [consultForm, setConsultForm] = useState({
    requestType: 'video',
    preferredDate: '',
    preferredTime: '10:00',
    topic: '',
    description: '',
    urgency: 'normal'
  });
  
  const [submitting, setSubmitting] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cset_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    fetchDashboard();
    fetchRecommendations();
    fetchMessages();
    fetchConsultations();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/virtual-ciso/dashboard`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setDashboard(data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/virtual-ciso/recommendations`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setRecommendations(data.recommendations || []);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/virtual-ciso/messages`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const fetchConsultations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/virtual-ciso/consultations`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setConsultations(data.consultations || []);
    } catch (error) {
      console.error('Failed to fetch consultations:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/virtual-ciso/messages`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(messageForm)
      });
      const data = await response.json();
      if (data.success) {
        alert(data.message);
        setShowMessageModal(false);
        setMessageForm({ subject: '', message: '', priority: 'normal', category: 'general' });
        fetchMessages();
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestConsultation = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/virtual-ciso/consultations`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(consultForm)
      });
      const data = await response.json();
      if (data.success) {
        alert(data.message);
        setShowConsultModal(false);
        setConsultForm({
          requestType: 'video',
          preferredDate: '',
          preferredTime: '10:00',
          topic: '',
          description: '',
          urgency: 'normal'
        });
        fetchConsultations();
      }
    } catch (error) {
      console.error('Failed to request consultation:', error);
      alert('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  // Color based on numeric score instead of letter grade
  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-400';
    if (score >= 80) return 'text-blue-400';
    if (score >= 70) return 'text-yellow-400';
    if (score >= 60) return 'text-orange-400';
    return 'text-red-400';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'scheduled': return 'bg-blue-500/20 text-blue-400';
      case 'completed': return 'bg-green-500/20 text-green-400';
      case 'cancelled': return 'bg-red-500/20 text-red-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-violet-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white">Virtual CISO</h1>
                  <p className="text-slate-400">Your dedicated security advisory service</p>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowMessageModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
                data-testid="contact-ciso-btn"
              >
                <MessageSquare className="w-5 h-5" />
                Contact CISO
              </button>
              <button
                onClick={() => setShowConsultModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                data-testid="schedule-call-btn"
              >
                <Calendar className="w-5 h-5" />
                Schedule Consultation
              </button>
            </div>
          </div>
        </div>

        {/* Quick Help Banner */}
        <div className="mb-6 p-4 bg-gradient-to-r from-violet-600/20 to-purple-600/20 border border-violet-500/30 rounded-xl">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <HeadphonesIcon className="w-10 h-10 text-violet-400" />
              <div>
                <h3 className="text-white font-semibold">Need Help? Your Virtual CISO is Here</h3>
                <p className="text-slate-400 text-sm">Get expert guidance on security strategy, compliance, and incident response</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { setMessageForm({ ...messageForm, priority: 'urgent', category: 'incident' }); setShowMessageModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm"
              >
                <AlertTriangle className="w-4 h-4" />
                Report Incident
              </button>
              <button
                onClick={() => setShowMessageModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
              >
                <Mail className="w-4 h-4" />
                Ask a Question
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: 'dashboard', label: 'Security Overview', icon: BarChart3 },
            { id: 'recommendations', label: 'Recommendations', icon: Lightbulb },
            { id: 'messages', label: 'Messages', icon: MessageSquare, count: messages.filter(m => m.status === 'pending').length },
            { id: 'consultations', label: 'Consultations', icon: Calendar, count: consultations.filter(c => c.status === 'pending' || c.status === 'scheduled').length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                activeTab === tab.id
                  ? 'bg-violet-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboard && (
          <div className="space-y-6">
            {/* Security Score Card */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1 bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-violet-400" />
                  Security Posture Score
                </h3>
                <div className="text-center">
                  <div className={`text-7xl font-bold ${getScoreColor(dashboard.securityScore?.score || 0)}`}>
                    {dashboard.securityScore?.score || 0}
                  </div>
                  <div className="text-2xl font-semibold text-slate-400 mt-2">out of 100</div>
                  <div className="text-slate-400 mt-1">{dashboard.securityScore?.label}</div>
                  
                  <div className="mt-6 space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-400">Compliance</span>
                        <span className="text-white">{dashboard.securityScore?.breakdown?.compliance}%</span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${dashboard.securityScore?.breakdown?.compliance}%` }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-400">Vulnerability Mgmt</span>
                        <span className="text-white">{dashboard.securityScore?.breakdown?.vulnerabilities}%</span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${dashboard.securityScore?.breakdown?.vulnerabilities}%` }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-400">Training</span>
                        <span className="text-white">{dashboard.securityScore?.breakdown?.training}%</span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full bg-purple-500 rounded-full" style={{ width: `${dashboard.securityScore?.breakdown?.training}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Risk Metrics */}
              <div className="lg:col-span-2 bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-red-400" />
                  Risk Metrics
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-center">
                    <div className="text-3xl font-bold text-red-400">{dashboard.riskMetrics?.highRiskVulnerabilities || 0}</div>
                    <div className="text-sm text-slate-400 mt-1">P1 Critical Vulns</div>
                  </div>
                  <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl text-center">
                    <div className="text-3xl font-bold text-orange-400">{dashboard.riskMetrics?.overdueRemediations || 0}</div>
                    <div className="text-sm text-slate-400 mt-1">Overdue Items</div>
                  </div>
                  <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl text-center">
                    <div className="text-3xl font-bold text-purple-400">{dashboard.riskMetrics?.criticalAssets || 0}</div>
                    <div className="text-sm text-slate-400 mt-1">Critical Assets</div>
                  </div>
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl text-center">
                    <div className="text-3xl font-bold text-blue-400">{dashboard.riskMetrics?.pendingAssessments || 0}</div>
                    <div className="text-sm text-slate-400 mt-1">Pending Assessments</div>
                  </div>
                </div>

                {/* Alert Summary */}
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-slate-400 mb-3">Vulnerability Alert Summary</h4>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                      <div className="text-xl font-semibold text-white">{dashboard.alertSummary?.total || 0}</div>
                      <div className="text-xs text-slate-500">Total</div>
                    </div>
                    <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                      <div className="text-xl font-semibold text-red-400">{dashboard.alertSummary?.open || 0}</div>
                      <div className="text-xs text-slate-500">Open</div>
                    </div>
                    <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                      <div className="text-xl font-semibold text-orange-400">{dashboard.alertSummary?.overdue || 0}</div>
                      <div className="text-xs text-slate-500">Overdue</div>
                    </div>
                    <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                      <div className="text-xl font-semibold text-green-400">{dashboard.alertSummary?.resolved || 0}</div>
                      <div className="text-xs text-slate-500">Resolved</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* P1 Critical Alerts */}
            {dashboard.p1Alerts?.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 text-red-400" />
                    P1 Critical Alerts - Immediate Action Required
                    <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-sm rounded">{dashboard.p1AlertCount}</span>
                  </h3>
                  <a href="/assets" className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1">
                    View All in Asset Inventory <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
                <div className="space-y-3">
                  {dashboard.p1Alerts.slice(0, 5).map((alert, idx) => (
                    <div key={idx} className="p-4 bg-slate-900/50 rounded-lg border-l-4 border-red-500">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded">
                            Score: {alert.priorityScore}
                          </span>
                          <a 
                            href={`https://nvd.nist.gov/vuln/detail/${alert.cveId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-red-400 hover:text-red-300 flex items-center gap-1"
                          >
                            {alert.cveId} <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                        <span className="text-xs text-slate-400">Due: {alert.dueDate}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">{alert.assetName}</p>
                          <p className="text-slate-400 text-sm">{alert.vendor} - {alert.product}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          alert.assetCriticality === 'critical' ? 'bg-red-500/20 text-red-400' :
                          alert.assetCriticality === 'high' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {alert.assetCriticality} asset
                        </span>
                      </div>
                      <p className="text-slate-300 text-sm mt-2 truncate">{alert.vulnerabilityName}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Compliance Summary */}
            {dashboard.complianceSummary?.length > 0 && (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" />
                  Active Assessments
                </h3>
                <div className="space-y-3">
                  {dashboard.complianceSummary.map((assessment, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="px-2 py-1 bg-violet-500/20 text-violet-400 text-xs rounded">{assessment.type}</span>
                        <span className="text-white">{assessment.name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-violet-500 rounded-full" 
                            style={{ width: `${assessment.progress}%` }}
                          />
                        </div>
                        <span className="text-sm text-slate-400 w-12">{assessment.progress}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recommendations Tab */}
        {activeTab === 'recommendations' && (
          <div className="space-y-4">
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 mb-6">
              <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-400" />
                CISO Recommendations
              </h3>
              <p className="text-slate-400 text-sm">Prioritized security actions based on your current posture</p>
            </div>

            {recommendations.map((rec, idx) => (
              <div 
                key={rec.id}
                className={`p-6 rounded-xl border ${getPriorityColor(rec.priority)}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${getPriorityColor(rec.priority)}`}>
                        {rec.priority}
                      </span>
                      <span className="text-slate-500 text-xs">{rec.category}</span>
                    </div>
                    <h4 className="text-white font-semibold text-lg mb-2">{rec.title}</h4>
                    <p className="text-slate-300 mb-4">{rec.description}</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="p-3 bg-slate-900/50 rounded-lg">
                        <div className="text-slate-500 mb-1">Recommended Action</div>
                        <div className="text-white">{rec.action}</div>
                      </div>
                      <div className="p-3 bg-slate-900/50 rounded-lg">
                        <div className="text-slate-500 mb-1">Expected Impact</div>
                        <div className="text-emerald-400">{rec.impact}</div>
                      </div>
                      <div className="p-3 bg-slate-900/50 rounded-lg">
                        <div className="text-slate-500 mb-1">Effort Level</div>
                        <div className="text-white capitalize">{rec.effort}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Message History</h3>
              <button
                onClick={() => setShowMessageModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm"
              >
                <Send className="w-4 h-4" />
                New Message
              </button>
            </div>

            {messages.length === 0 ? (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-12 text-center">
                <MessageSquare className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h4 className="text-xl font-semibold text-white mb-2">No Messages Yet</h4>
                <p className="text-slate-400 mb-6">Start a conversation with your Virtual CISO</p>
                <button
                  onClick={() => setShowMessageModal(true)}
                  className="px-6 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg"
                >
                  Send First Message
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((msg) => (
                  <div key={msg.id} className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          msg.priority === 'critical' ? 'bg-red-500/20 text-red-400' :
                          msg.priority === 'urgent' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          {msg.priority}
                        </span>
                        <span className="text-xs text-slate-500">{msg.category}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(msg.status)}`}>
                          {msg.status}
                        </span>
                        <span className="text-xs text-slate-500">{new Date(msg.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <h4 className="text-white font-medium mb-1">{msg.subject}</h4>
                    <p className="text-slate-400 text-sm">{msg.message}</p>
                    {msg.responses?.length > 0 && (
                      <div className="mt-4 p-3 bg-violet-500/10 border border-violet-500/30 rounded-lg">
                        <div className="text-xs text-violet-400 mb-1">CISO Response</div>
                        <p className="text-slate-300 text-sm">{msg.responses[msg.responses.length - 1].message}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Consultations Tab */}
        {activeTab === 'consultations' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Consultation History</h3>
              <button
                onClick={() => setShowConsultModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm"
              >
                <Calendar className="w-4 h-4" />
                Schedule New
              </button>
            </div>

            {consultations.length === 0 ? (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-12 text-center">
                <Calendar className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h4 className="text-xl font-semibold text-white mb-2">No Consultations Scheduled</h4>
                <p className="text-slate-400 mb-6">Book time with your Virtual CISO for in-depth guidance</p>
                <button
                  onClick={() => setShowConsultModal(true)}
                  className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                >
                  Schedule Consultation
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {consultations.map((consult) => (
                  <div key={consult.id} className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {consult.requestType === 'video' && <Video className="w-5 h-5 text-blue-400" />}
                        {consult.requestType === 'call' && <Phone className="w-5 h-5 text-green-400" />}
                        {consult.requestType === 'email' && <Mail className="w-5 h-5 text-purple-400" />}
                        {consult.requestType === 'onsite' && <MapPin className="w-5 h-5 text-orange-400" />}
                        <span className="text-white font-medium capitalize">{consult.requestType} Consultation</span>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(consult.status)}`}>
                        {consult.status}
                      </span>
                    </div>
                    <h4 className="text-white font-medium mb-1">{consult.topic}</h4>
                    <p className="text-slate-400 text-sm mb-3">{consult.description}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {consult.confirmedDate || consult.preferredDate}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {consult.confirmedTime || consult.preferredTime}
                      </span>
                    </div>
                    {consult.meetingLink && (
                      <a 
                        href={consult.meetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                      >
                        <Video className="w-4 h-4" />
                        Join Meeting
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Message Modal */}
        {showMessageModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-violet-400" />
                  Contact Your Virtual CISO
                </h2>
                <button onClick={() => setShowMessageModal(false)} className="text-slate-400 hover:text-white">
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleSendMessage} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Subject *</label>
                  <input
                    type="text"
                    value={messageForm.subject}
                    onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                    required
                    placeholder="e.g., Question about compliance requirements"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Priority</label>
                    <select
                      value={messageForm.priority}
                      onChange={(e) => setMessageForm({ ...messageForm, priority: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                    >
                      <option value="normal">Normal</option>
                      <option value="urgent">Urgent</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Category</label>
                    <select
                      value={messageForm.category}
                      onChange={(e) => setMessageForm({ ...messageForm, category: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                    >
                      <option value="general">General Question</option>
                      <option value="compliance">Compliance</option>
                      <option value="incident">Security Incident</option>
                      <option value="vulnerability">Vulnerability</option>
                      <option value="strategy">Security Strategy</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Message *</label>
                  <textarea
                    value={messageForm.message}
                    onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                    required
                    rows={5}
                    placeholder="Describe your question or concern..."
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white resize-none"
                  />
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm text-blue-300">
                  <Clock className="w-4 h-4 inline mr-2" />
                  Your Virtual CISO typically responds within 24 hours. For urgent matters, select "Critical" priority.
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  Send Message
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Consultation Modal */}
        {showConsultModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl p-6 w-full max-w-lg border border-slate-700 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-emerald-400" />
                  Schedule CISO Consultation
                </h2>
                <button onClick={() => setShowConsultModal(false)} className="text-slate-400 hover:text-white">
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleRequestConsultation} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Consultation Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { id: 'video', icon: Video, label: 'Video Call', color: 'blue' },
                      { id: 'call', icon: Phone, label: 'Phone Call', color: 'green' },
                      { id: 'email', icon: Mail, label: 'Email Review', color: 'purple' },
                      { id: 'onsite', icon: MapPin, label: 'On-Site Visit', color: 'orange' }
                    ].map(type => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setConsultForm({ ...consultForm, requestType: type.id })}
                        className={`p-3 rounded-lg border text-left transition-colors ${
                          consultForm.requestType === type.id
                            ? `bg-${type.color}-500/20 border-${type.color}-500/50 text-white`
                            : 'bg-slate-900 border-slate-600 text-slate-400 hover:border-slate-500'
                        }`}
                      >
                        <type.icon className={`w-5 h-5 mb-1 ${consultForm.requestType === type.id ? `text-${type.color}-400` : ''}`} />
                        <div className="text-sm font-medium">{type.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Preferred Date *</label>
                    <input
                      type="date"
                      value={consultForm.preferredDate}
                      onChange={(e) => setConsultForm({ ...consultForm, preferredDate: e.target.value })}
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Preferred Time *</label>
                    <select
                      value={consultForm.preferredTime}
                      onChange={(e) => setConsultForm({ ...consultForm, preferredTime: e.target.value })}
                      required
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                    >
                      {['09:00', '10:00', '11:00', '13:00', '14:00', '15:00', '16:00'].map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Topic *</label>
                  <input
                    type="text"
                    value={consultForm.topic}
                    onChange={(e) => setConsultForm({ ...consultForm, topic: e.target.value })}
                    required
                    placeholder="e.g., CMMC Certification Roadmap"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Description</label>
                  <textarea
                    value={consultForm.description}
                    onChange={(e) => setConsultForm({ ...consultForm, description: e.target.value })}
                    rows={3}
                    placeholder="Briefly describe what you'd like to discuss..."
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Urgency</label>
                  <select
                    value={consultForm.urgency}
                    onChange={(e) => setConsultForm({ ...consultForm, urgency: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  >
                    <option value="normal">Normal (within 1 week)</option>
                    <option value="urgent">Urgent (within 48 hours)</option>
                    <option value="emergency">Emergency (same day if possible)</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Calendar className="w-5 h-5" />}
                  Request Consultation
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VirtualCISO;
